import type { LayoutOptions, NodeSingular } from 'cytoscape'
import type { MockEdge, MockNode } from './types'

/**
 * The two canvas layouts the toolbar offers (DSGN-63 Phase 1).
 *
 * - `tree`: cytoscape `breadthfirst`, top-down, parents above children —
 *   the rules in the designer's Tree View note.
 * - `radial`: cytoscape `concentric`, the hierarchy roots in the middle and
 *   each level of depth on its own ring — the hub-and-spoke of the Radial
 *   View note.
 *
 * Kept out of the canvas component so the depth walk and the option objects
 * are unit-testable without a cytoscape instance (the same reason
 * `computeRootIds.ts` sits beside it).
 */
export type GraphLayout = 'radial' | 'tree'

export const DEFAULT_GRAPH_LAYOUT: GraphLayout = 'radial'

export const isGraphLayout = (value: unknown): value is GraphLayout => value === 'radial' || value === 'tree'

/**
 * Distance of every node from the nearest root, following edges source →
 * target. Nodes no root reaches (orphans, or a disconnected cycle) sit one
 * level past the deepest reached node, so they land on the outermost ring
 * rather than crashing the hub.
 */
export const computeDepths = (nodes: MockNode[], edges: MockEdge[], rootIds: string[]): Map<string, number> => {
  const children = new Map<string, string[]>()
  for (const e of edges) {
    const list = children.get(e.source)
    if (list) list.push(e.target)
    else children.set(e.source, [e.target])
  }
  const depth = new Map<string, number>()
  const queue: string[] = []
  for (const id of rootIds) {
    if (depth.has(id)) continue
    depth.set(id, 0)
    queue.push(id)
  }
  while (queue.length > 0) {
    const id = queue.shift() as string
    const d = depth.get(id) as number
    for (const child of children.get(id) ?? []) {
      if (depth.has(child)) continue
      depth.set(child, d + 1)
      queue.push(child)
    }
  }
  let deepest = 0
  depth.forEach((d) => {
    if (d > deepest) deepest = d
  })
  for (const n of nodes) {
    if (!depth.has(n.id)) depth.set(n.id, deepest + 1)
  }
  return depth
}

interface BuildArgs {
  layout: GraphLayout
  nodes: MockNode[]
  edges: MockEdge[]
  rootIds: string[]
  /** Glide nodes to their new places; off for the first paint. */
  animate: boolean
}

export const LAYOUT_ANIMATION_MS = 400

export const buildLayoutOptions = ({ layout, nodes, edges, rootIds, animate }: BuildArgs): LayoutOptions => {
  const common = {
    padding: 40,
    animate,
    animationDuration: animate ? LAYOUT_ANIMATION_MS : 0,
    animationEasing: 'ease-out' as const,
  }
  if (layout === 'tree') {
    return {
      name: 'breadthfirst',
      directed: true,
      roots: rootIds.length > 0 ? rootIds : undefined,
      spacingFactor: 1.2,
      ...common,
    }
  }
  const depths = computeDepths(nodes, edges, rootIds)
  let deepest = 0
  depths.forEach((d) => {
    if (d > deepest) deepest = d
  })
  return {
    name: 'concentric',
    // Higher value = closer to the centre, so invert the depth.
    concentric: (node: NodeSingular) => deepest - (depths.get(node.id()) ?? deepest),
    // One ring per depth, not per distinct value range.
    levelWidth: () => 1,
    equidistant: true,
    minNodeSpacing: 40,
    startAngle: (3 / 2) * Math.PI,
    ...common,
  }
}
